import bg32 from 'assets/img/bg/bg-32.png';
import { getEntityId } from 'helpers/entityId';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Avatar, { Size } from 'components/base/Avatar';
import Button from 'components/base/Button';
import FeatherIcon from 'feather-icons-react';
import { Dropdown, Nav } from 'react-bootstrap';
import { Employee, Member } from 'data/users';
import {
  faChevronRight,
  faCircle,
  faEllipsis,
  faEnvelope,
  faMessage,
  faPhone,
  faUserPlus,
  faVideo
} from '@fortawesome/free-solid-svg-icons';
import EmployeeAvatar from 'components/base/EmployeeGravatar';
import UnassignButton from './Action/UnassignButton';

interface AvatarDropdownInterface {
  user: Member;
  size: Size;
}

const AvatarDropdown = ({ user, size }: AvatarDropdownInterface) => {
  return (
    <Dropdown>
      <Dropdown.Toggle variant="" className="dropdown-caret-none p-0">
        <Avatar src={user?.avatar} size={size} />
      </Dropdown.Toggle>

      <Dropdown.Menu
        className="avatar-dropdown-menu p-0 overflow-hidden"
        popperConfig={{ strategy: 'fixed' }}
        renderOnMount
      >
        <div className="position-relative">
          <div
            className="bg-holder z-index--1"
            style={{
              backgroundImage: `url(${bg32})`,
              backgroundSize: 'auto'
            }}
          />
          {/*/.bg-holder*/}
          <div className="p-3">
            <div className="text-end">
              <button className="btn p-0 me-2">
                <FontAwesomeIcon
                  icon={faUserPlus}
                  className="text-white light"
                />
              </button>
              <button className="btn p-0">
                <FontAwesomeIcon
                  icon={faEllipsis}
                  className="text-white light"
                />
              </button>
            </div>
            <div className="text-center">
              <Avatar
                src={user?.avatar}
                size="xl"
                status="online"
                className="border border-white rounded-circle mx-auto mb-2"
              />
              <h6 className="text-white light">{user?.name}</h6>
              <p className="text-600 fw-semi-bold fs-10 mb-2">
                @{user?.username}
              </p>
              <div className="d-flex flex-center mb-3">
                <h6 className="text-white light mb-0">
                  {user?.connections}{' '}
                  <span className="fw-normal text-300">connections</span>
                </h6>
                <FontAwesomeIcon
                  icon={faCircle}
                  className="text-700 mx-1 fs-9"
                  transform="shrink-10 up-2"
                />
                <h6 className="text-white light mb-0">
                  {user?.mutual}{' '}
                  <span className="fw-normal text-300">mutual</span>
                </h6>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white">
          <div className="p-3 border-bottom">
            <div className="d-flex gap-2">
              <Button
                variant="phoenix-secondary"
                className="btn-icon btn-icon-lg"
              >
                <FontAwesomeIcon icon={faPhone} />
              </Button>
              <Button
                variant="phoenix-secondary"
                className="btn-icon btn-icon-lg"
              >
                <FontAwesomeIcon icon={faMessage} />
              </Button>
              <Button
                variant="phoenix-secondary"
                className="btn-icon btn-icon-lg"
              >
                <FontAwesomeIcon icon={faVideo} />
              </Button>
              <Button
                variant="phoenix-primary"
                startIcon={
                  <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                }
                className="ms-auto"
              >
                Send Email
              </Button>
            </div>
          </div>
          <Nav className="flex-column py-3 border-bottom">
            <Nav.Link className="">
              <FeatherIcon
                icon="clipboard"
                size={16}
                className="me-2 text-900 d-inline-block"
              />
              <span className="text-1000 flex-1">Assigned Projects</span>
              <FontAwesomeIcon icon={faChevronRight} className="fs-11" />
            </Nav.Link>
            <Nav.Link className="">
              <FeatherIcon
                icon="pie-chart"
                size={16}
                className="me-2 text-900 d-inline-block"
              />
              <span className="text-1000 flex-1">View activity</span>
              <FontAwesomeIcon icon={faChevronRight} className="fs-11" />
            </Nav.Link>
          </Nav>

          <div className="p-3 d-flex justify-content-between">
            <Button variant="link" className="p-0 text-decoration-none">
              Details
            </Button>
            <Button
              variant="link"
              className="p-0 text-decoration-none text-danger"
            >
              Unassign
            </Button>
          </div>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default AvatarDropdown;

interface EmployeeAvatarDropdownInterface {
  user: Employee;
  jobId?: string;
  supervisorId?: string;
  size: Size;
}

export const EmployeeAvatarDropdown = ({
  user,
  jobId,
  supervisorId,
  size
}: EmployeeAvatarDropdownInterface) => {
  const handleClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    email: string
  ) => {
    e.preventDefault();
    const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(
      'Create A New Subject'
    )}&body=${encodeURIComponent('And Type the Body of the email')}`;
    window.location.href = mailtoLink;
  };
  return (
    <Dropdown>
      <Dropdown.Toggle variant="" className="dropdown-caret-none p-0">
        <EmployeeAvatar src={user?.email} size={size} />
      </Dropdown.Toggle>

      <Dropdown.Menu
        className="avatar-dropdown-menu p-0 overflow-hidden"
        popperConfig={{ strategy: 'fixed' }}
        renderOnMount
      >
        <div className="position-relative">
          <div
            className="bg-holder z-index--1"
            style={{
              backgroundImage: `url(${bg32})`,
              backgroundSize: 'auto'
            }}
          />
          {/*/.bg-holder*/}
          <div className="p-3">
            <div className="text-end">
              <button className="btn p-0 me-2">
                <FontAwesomeIcon
                  icon={faUserPlus}
                  className="text-white light"
                />
              </button>
              <button className="btn p-0">
                <FontAwesomeIcon
                  icon={faEllipsis}
                  className="text-white light"
                />
              </button>
            </div>
            <div className="text-center">
              <EmployeeAvatar
                src={user?.email}
                size="xl"
                status="online"
                className="border border-white rounded-circle mx-auto mb-2"
              />
              <h6 className="text-white light">
                {user?.firstName} {user?.lastName}
              </h6>
              <p className="text-600 fw-semi-bold fs-10 mb-2">
                @{user?.username}
              </p>
              {/* <div className="d-flex flex-center mb-3">
                <h6 className="text-white light mb-0">
                  {user?.firstName?.length}{' '}
                  <span className="fw-normal text-300">connections</span>
                </h6>
                <FontAwesomeIcon
                  icon={faCircle}
                  className="text-700 mx-1 fs-9"
                  transform="shrink-10 up-2"
                />
                <h6 className="text-white light mb-0">
                  {user?.lastName?.length}{' '}
                  <span className="fw-normal text-300">mutual</span>
                </h6>
              </div> */}
            </div>
          </div>
        </div>
        <div className="bg-white">
          <div className="p-3 border-bottom">
            <div className="d-flex gap-2">
              {/* <Button
                variant="phoenix-secondary"
                className="btn-icon btn-icon-lg"
              >
                <FontAwesomeIcon icon={faPhone} />
              </Button>
              <Button
                variant="phoenix-secondary"
                className="btn-icon btn-icon-lg"
              >
                <FontAwesomeIcon icon={faMessage} />
              </Button>
              <Button
                variant="phoenix-secondary"
                className="btn-icon btn-icon-lg"
              >
                <FontAwesomeIcon icon={faVideo} />
              </Button> */}
              <Button
                variant="phoenix-primary"
                startIcon={
                  <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                }
                className="ms-auto"
                onClick={e => handleClick(e, user?.email)}
              >
                Send Email
              </Button>
            </div>
          </div>
          {/* <Nav className="flex-column py-3 border-bottom">
            <Nav.Link className="">
              <FeatherIcon
                icon="clipboard"
                size={16}
                className="me-2 text-900 d-inline-block"
              />
              <span className="text-1000 flex-1">Assigned Projects</span>
              <FontAwesomeIcon icon={faChevronRight} className="fs-11" />
            </Nav.Link>
            <Nav.Link className="">
              <FeatherIcon
                icon="pie-chart"
                size={16}
                className="me-2 text-900 d-inline-block"
              />
              <span className="text-1000 flex-1">View activity</span>
              <FontAwesomeIcon icon={faChevronRight} className="fs-11" />
            </Nav.Link>
          </Nav> */}

          <div className="p-3 d-flex justify-content-between">
            {/* <Button variant="link" className="p-0 text-decoration-none">
              Details
            </Button> */}
            <UnassignButton
              userId={getEntityId(user) || ''}
              jobId={jobId}
              supervisorId={supervisorId}
            />
          </div>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};
